import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { EstatePageSuggestionSummary } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsV2Query } from '@/modules/estate/estateAIOnboarding/graphql/EstateOnboardingUnacknowledgedSuggestionsV2.generated';
import { useEstatePageDataQuery } from '@/modules/estate/graphql/EstatePageData.generated';
import { getEstatePageSuggestionWhere } from '@/modules/estate/graphql/EstatePageData.utils';
import { useRequiredHouseholdId } from '@/modules/household/contexts/useRequiredHouseholdId';
import { EntityKind, EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { ReviewSuggestionCountsDocument } from './ReviewSuggestions.generated';

export function useReviewSuggestionCategoryData({
  entityKinds,
  includeOnboardingSuggestions,
  includeTestamentaryEntities,
}: {
  entityKinds: readonly EntityKind[];
  includeOnboardingSuggestions: boolean;
  includeTestamentaryEntities: boolean;
}) {
  const refetchCounts = useRefetchReviewSuggestionCounts();
  const householdId = useRequiredHouseholdId();
  const suggestionEntityWhere = {
    hasHouseholdWith: [{ id: householdId }],
    kindIn: [...entityKinds],
    stageIn: [
      EntityStage.Active,
      EntityStage.PreCreated,
      EntityStage.Completed,
    ],
  };
  const suggestionTargetWhere = includeTestamentaryEntities
    ? {
        or: [
          { hasEntityWith: [suggestionEntityWhere] },
          { hasTestamentaryEntityWith: [{ householdID: householdId }] },
        ],
      }
    : { hasEntityWith: [suggestionEntityWhere] };
  const suggestionWhere = getEstatePageSuggestionWhere(householdId, {
    detailTargetWhere: suggestionTargetWhere,
    holdingTargetWhere: suggestionTargetWhere,
  });

  const {
    data: estateData,
    error: estateError,
    loading: estateLoading,
    refetch: refetchEstateQuery,
  } = useEstatePageDataQuery({
    variables: {
      householdId,
      entitiesLike: {
        hasHouseholdWith: [{ id: householdId }],
        kindIn: [...entityKinds],
        stageIn: [
          EntityStage.Active,
          EntityStage.PreCreated,
          EntityStage.Completed,
        ],
      },
      ...suggestionWhere,
      includeDraftEntities: false,
      includeTestamentaryEntities,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const {
    data: onboardingData,
    error: onboardingError,
    loading: onboardingLoading,
    refetch: refetchOnboardingQuery,
  } = useEstateOnboardingUnacknowledgedSuggestionsV2Query({
    skip: !includeOnboardingSuggestions,
    variables: { householdID: householdId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const refetch = useCallback(() => {
    void refetchEstateQuery();
    if (includeOnboardingSuggestions) void refetchOnboardingQuery();
    refetchCounts();
  }, [
    includeOnboardingSuggestions,
    refetchCounts,
    refetchEstateQuery,
    refetchOnboardingQuery,
  ]);

  return {
    entities: getNodes(estateData?.entities),
    error: estateError ?? onboardingError,
    loading: estateLoading || onboardingLoading,
    onboardingSuggestions: getNodes(
      onboardingData?.aiSuggestions
    ) as AiSuggestionForReviewCard[],
    refetch,
    suggestions: [
      ...getNodes(estateData?.aiSuggestions),
      ...getNodes(estateData?.holdingSuggestions),
    ] as EstatePageSuggestionSummary[],
    testamentaryEntities: getNodes(estateData?.testamentaryEntities),
  };
}

export function useRefetchReviewSuggestionCounts() {
  const apolloClient = useApolloClient();

  return useCallback(() => {
    void apolloClient.refetchQueries({
      include: [ReviewSuggestionCountsDocument],
    });
  }, [apolloClient]);
}
